"use client";

import { useLocation, useNavigate, useParams } from "react-router-dom";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useTranslation } from "react-i18next";

import { axiosApiInstance } from "@/libs/axios.tsx";
import { API_ENDPOINTS } from "@/constants/api.ts";
import PATHS from "@/routes/paths.ts";
import { useProfileDataStore } from "@/features/profile/tutor/store/useProfileDataStore.tsx";
import ProfileLoader from "@/wrappers/GetProfileDataWrapper/ProfileLoader.tsx";
import AppLoader from "@/components/AppLoader.tsx";

interface GetProfileDataWrapperProps {
  children: ReactNode;
}

export default function GetProfileDataWrapper({ children }: GetProfileDataWrapperProps) {
  const redirect = useNavigate();
  const { t } = useTranslation(["profile"]);
  const location = useLocation();
  const {
    hasProfile,
    profileData,
    setHasProfile,
    setProfileData,
    settingsData,
    toReload,
    setToReload,
    setSettingsData,
  } = useProfileDataStore();
  let { locale } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isFinalDestination, setIsFinalDestination] = useState(false);

  const getData = async () => {
    try {
      setIsLoading(true);
      const { data: settingsData } = await axiosApiInstance.get(
        `${API_ENDPOINTS.TUTOR.PROFILE.GET_SETTINGS}`,
      );

      setSettingsData(settingsData.data);
      const { data } = await axiosApiInstance.get(
        `${API_ENDPOINTS.TUTOR.PROFILE.GET_PROFILE_DATA}`,
        {
          params: { locale_code: locale ?? settingsData.data.main_profile.main_profile_locale },
        },
      );

      setProfileData(data.data);
      setHasProfile(true);
      setToReload(false);
      if (location.pathname !== PATHS.TUTOR.CREATE_PROFILE_NL && settingsData && !locale) {
        redirect(
          `${PATHS.TUTOR.PUBLIC_PROFILE}/${settingsData.data.main_profile.main_profile_locale}`,
        );
        setIsFinalDestination(true);
      }
    } catch (error: any) {
      console.log(error);
      console.log(error.response.data.code);
      error.response.data.code === "tutor_without_profiles" && setHasProfile(false);
      redirect(PATHS.TUTOR.CREATE_PROFILE);
      setIsFinalDestination(true);
    } finally {
      setTimeout(() => setIsLoading(false), 300);
    }
  };
  const debouncedGetData = useDebouncedCallback(() => {
    getData();
  }, 300);
  const initialLoadRef = useRef(false);

  useEffect(() => {
    if (
      profileData?.header_section?.is_using_fallback &&
      profileData?.header_section.requested_locale_code === locale
    )
      return;
    const shouldFetchData =
      (!initialLoadRef.current && hasProfile === undefined) ||
      toReload ||
      (locale?.toLowerCase() !== profileData?.header_section.locale_code.toLowerCase() &&
        profileData &&
        locale);

    if (shouldFetchData) {
      debouncedGetData();
      initialLoadRef.current = true;
    }
  }, [hasProfile, toReload, locale, profileData]);

  useEffect(() => {
    if (hasProfile === false && location.pathname !== PATHS.TUTOR.CREATE_PROFILE) {
      redirect(PATHS.REDIRECT);
      setIsFinalDestination(true);
    }
    if (hasProfile && location.pathname !== PATHS.TUTOR.CREATE_PROFILE_NL) {
      redirect(
        `${PATHS.TUTOR.PUBLIC_PROFILE}/${locale ? locale : settingsData?.main_profile.main_profile_locale}`,
      );
      setIsFinalDestination(true);
    }
    if (hasProfile && location.pathname === PATHS.TUTOR.CREATE_PROFILE_NL) {
      setIsFinalDestination(true);
    }
  }, [location.pathname, hasProfile, settingsData, locale]);
  if (isLoading && hasProfile && !location.pathname.includes(PATHS.TUTOR.CREATE_PROFILE_NL)) {
    return <ProfileLoader />;
  } else if (isLoading && !hasProfile) {
    return (
      <div className={"fixed top-0 left-0 w-full overflow-hidden"}>
        <title>{`${t("profilePage")} - Ximly`}</title>
        <AppLoader />
      </div>
    );
  }

  if (isFinalDestination) {
    return children;
  }
}
